import React, { useState, useEffect, useRef } from 'react';
import {
    fetchDashboardData // Import only the new consolidated fetch function
} from '../api/snippets';
import ClassificationChart from '../components/ClassificationChart'; // Uses the version without internal filtering

// Define positions including 'All' for the filter UI
const POSITIONS = ['All', 'QB', 'RB', 'WR', 'TE'];

// --- Simple In-Memory Cache ---
const dataCache = {};
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
// --- End Cache ---

const MainMenuPage = () => {
    // --- Refactored State: Use a single object for all chart data ---
    const [chartData, setChartData] = useState({
        byClass: {},    // For classification charts
        overall: [],    // For "Most Discussed Players"
        positive: [],   // For "Most Positive Sentiment"
        negative: [],   // For "Most Negative Sentiment"
        highValue: []   // For "Most High Value"
    });
    // --- End Refactored State ---

    // State for filters
    const [days, setDays] = useState(7); // Default date filter
    const [selectedPosition, setSelectedPosition] = useState('All'); // Global position filter state

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Date options for button group
    const dateOptions = [
        { label: '1D', value: 1 }, { label: '3D', value: 3 }, { label: '7D', value: 7 },
        { label: '30D', value: 30 }, { label: 'All', value: 0 },
    ];

    // Handlers for filters
    const handleDaysButtonClick = (value) => { if (!isLoading) { setDays(value); } };
    const handlePositionFilterClick = (position) => { if (!isLoading) { setSelectedPosition(position); } };

    // Effect to load data based on selected filters
    useEffect(() => {
        const cacheKey = `${days}-${selectedPosition}`;
        const cached = dataCache[cacheKey];

        if (cached && (Date.now() - cached.timestamp < CACHE_DURATION_MS)) {
            // console.log("Serving from cache:", cacheKey);
            setChartData(cached.data);
            setIsLoading(false);
            setError(null);
            return; // Exit early if fresh cached data is available
        }
        // console.log("Cache miss or stale, fetching data:", cacheKey);

        let isMounted = true; // To prevent state updates on unmounted component
        const loadAllData = async () => {
            setIsLoading(true);
            setError(null);
            const positionFilterForApi = selectedPosition === 'All' ? null : selectedPosition;

            try {
                // Fetch all data using the single consolidated endpoint
                const dashboardData = await fetchDashboardData(days, positionFilterForApi);

                if (!isMounted) return; // Component was unmounted during fetch

                // The structure of dashboardData should already match what setChartData expects
                // { byClass: {}, overall: [], positive: [], negative: [], highValue: [] }
                setChartData(dashboardData);
                dataCache[cacheKey] = { data: dashboardData, timestamp: Date.now() }; // Update cache

                // Since fetchDashboardData handles its own errors and returns a default structure,
                // we might not have specific errors to aggregate here unless fetchDashboardData re-throws.
                // If fetchDashboardData throws an error (e.g., network issue before it can return a default),
                // the catch block below will handle it.

            } catch (err) { // This will catch errors if fetchDashboardData itself fails catastrophically
                if (!isMounted) return;
                setError('Failed to load dashboard data. Please try again later.');
                console.error("Unexpected error during dashboard data load:", err);
                // Set to a default empty state, or use stale cache if available
                setChartData(cached?.data || { byClass: {}, overall: [], positive: [], negative: [], highValue: [] });
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadAllData();

        return () => {
            isMounted = false; // Cleanup function to set isMounted to false when component unmounts
        };
    }, [days, selectedPosition]); // useEffect depends on both filters

    // Play All handler (no change needed)
    const handlePlayAll = (title, players) => {
        console.log(`Initiating Play All for ${title}`, players);
        alert(`Play All for ${title} clicked. Feature not fully implemented.`);
    }

    // --- Define the desired fixed order for ALL charts ---
     const desiredChartOrder = [
        "Most Discussed Players",
        "Most Positive Sentiment",
        "Most Negative Sentiment",
        "Most High Value",
        "Performance",
        "Rankings",
        "Injury",
        "Waivers",
        "Trade",
        // "Neutral" // Add back if needed
    ];

    // --- Refactored getChartData to use single state object ---
    const getChartData = (title) => {
        // Check classification data first
        if (chartData.byClass && chartData.byClass[title]) {
            return chartData.byClass[title];
        }
        // Check other chart types
        switch(title) {
            case "Most Discussed Players": return chartData.overall;
            case "Most Positive Sentiment": return chartData.positive;
            case "Most Negative Sentiment": return chartData.negative;
            case "Most High Value": return chartData.highValue;
            default: return []; // Return empty if title not found
        }
    }
    // --- End refactored getChartData ---

    return (
        <div className="p-4 md:p-6 bg-base-200 min-h-screen">
            {/* Logo Image */}
            <img src="/twifwave.png" alt="The Week In Fantasy Logo" className="block mx-auto h-16 md:h-32 w-auto mb-4"/>

            {/* --- Filter Controls Area --- */}
            <div className="mb-6 flex flex-wrap justify-center md:justify-end items-center gap-x-4 sm:gap-x-6 gap-y-2">
                 {/* Date Filter Button Group */}
                 <div className="flex items-center">
                    <span className="mr-1 text-sm font-medium text-base-content/80">Last:</span>
                    <div className="btn-group">
                        {dateOptions.map(option => ( <button key={option.value} className={`btn btn-sm ${days === option.value ? 'btn-active btn-primary' : 'btn-ghost'}`} onClick={() => handleDaysButtonClick(option.value)} disabled={isLoading}>{option.label}</button> ))}
                    </div>
                </div>
                 {/* Position Filter Button Group */}
                 <div className="flex items-center">
                    <span className="mr-1 text-sm font-medium text-base-content/80">Pos:</span>
                    <div className="btn-group">
                        {POSITIONS.map(pos => ( <button key={pos} className={`btn btn-sm ${selectedPosition === pos ? 'btn-active btn-secondary' : 'btn-ghost'}`} onClick={() => handlePositionFilterClick(pos)} disabled={isLoading}> {pos} </button> ))}
                    </div>
                </div>
            </div>
            {/* --- End Filters --- */}

            {/* Loading State */}
            {isLoading && ( <div className="text-center py-10"><span className="loading loading-lg loading-spinner text-primary"></span><p>Loading Top Players...</p></div> )}

            {/* Error State */}
            {error && !isLoading && ( <div role="alert" className="alert alert-error shadow-lg max-w-md mx-auto"><svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg><span>{error}</span></div> )}

            {/* Charts Grid */}
            {!isLoading && !error && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {desiredChartOrder.map((title) => {
                         // getChartData now reads from the single chartData state object
                         const playersData = getChartData(title);
                         return ( <ClassificationChart key={title} title={title} players={playersData} onPlayAll={handlePlayAll} /> );
                       }
                    )}
                    {desiredChartOrder.length === 0 && ( <p className="text-center text-base-content text-opacity-70 md:col-span-2 lg:col-span-3 py-10"> No charts configured for display. </p> )}
                 </div>
            )}
        </div>
    );
};

export default MainMenuPage;